import { useEffect, useRef, useState } from 'react';
import { useAtlas } from '../app/store.ts';
import { Button } from '../ui/primitives.tsx';
import { BROWSERS, NOTICE_SEEN, NOTICE_TITLE, noticeWhy } from './preview.ts';

const seen = () => {
  try {
    return sessionStorage.getItem(NOTICE_SEEN) === '1';
  } catch {
    return false;
  }
};

/**
 * Where the wasm viewport can't run (engine.fallback), the page draws the 2D preview and says why, with
 * the browsers that draw the 3D view. Spec §11's dialog: 440 wide, a title, one paragraph and two buttons
 * right-aligned, the primary last. It sits over the picture until it's dismissed; the HUD keeps a short
 * note after that.
 */
export function GpuNotice() {
  const fallback = useAtlas((s) => (s.engine.kind === 'mock' ? (s.engine.fallback ?? null) : null));
  const [open, setOpen] = useState(() => !seen());
  const [copied, setCopied] = useState<'yes' | 'no' | null>(null);
  const primary = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (fallback && open) primary.current?.focus();
  }, [fallback, open]);
  if (!fallback || !open) return null;
  const dismiss = () => {
    try {
      sessionStorage.setItem(NOTICE_SEEN, '1');
    } catch {
      // Storage refused (a private window, say): the notice comes back on a reload.
    }
    setOpen(false);
  };
  const copy = () =>
    navigator.clipboard?.writeText(location.href).then(
      () => setCopied('yes'),
      () => setCopied('no'),
    ) ?? setCopied('no');
  return (
    <div className="sheet-overlay" role="presentation">
      <div
        className="sheet gpu-notice"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="gpu-notice-title"
        aria-describedby="gpu-notice-why"
        data-label-exclude=""
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            dismiss();
          }
        }}
      >
        <h2 id="gpu-notice-title">{NOTICE_TITLE[fallback.why]}</h2>
        <p id="gpu-notice-why">
          {noticeWhy(fallback)} {fallback.why === 'no-webgpu' || fallback.why === 'no-adapter' ? `${BROWSERS} ` : ''}
          The 2D preview keeps every scene’s panels, traces and Methods; its picture is a flat sketch of the structures.
        </p>
        <div className="gpu-notice-actions">
          <Button variant="quiet" onClick={() => void copy()}>
            {copied === 'yes' ? 'Link copied' : copied === 'no' ? 'Couldn’t copy the link' : 'Copy link'}
          </Button>
          <Button ref={primary} variant="primary" onClick={dismiss}>
            Continue in 2D
          </Button>
        </div>
      </div>
    </div>
  );
}
